// ============================================================================
// CHANGO V5.0 — Excel Import Modal (通用) · 简化版（直接导入 · 无 dry-run）
// ============================================================================
// Task #110 · 客户、产品、报价、物料、工序批量导入（从 3 步改为 2 步）
// Props:
//   - entity: 'customers' | 'products' | 'quotes' | 'bomItems' | 'processes'
//   - onComplete: () => void  导入完成时调用（用于刷新列表）
//   - onClose: () => void     用户取消/关闭弹窗
//
// 新流程：
//   Step 1：下载模板 + 上传文件 → 直接导入
//   Step 2：显示结果（成功/失败）
// ============================================================================

import React, { useState } from 'react';
import { api, HttpError } from '../api/client.js';

const ENTITY_LABELS = {
  customers: { label: '客户', path: '/api/customers', filename: 'customers-import-template.xlsx' },
  products: { label: '产品', path: '/api/products', filename: 'products-import-template.xlsx' },
  quotes: { label: '报价记录', path: '/api/quotes', filename: 'quotes-import-template.xlsx' },
  bomItems: { label: '物料', path: '/api/bom-items', filename: 'bom-items-import-template.xlsx' },
  processes: { label: '工序', path: '/api/processes', filename: 'processes-import-template.xlsx' }
};

export default function ExcelImportModal({ entity, onComplete, onClose }) {
  if (!ENTITY_LABELS[entity]) {
    throw new Error(`Unknown entity: ${entity}`);
  }

  const config = ENTITY_LABELS[entity];
  const [step, setStep] = useState(1); // 1: upload, 2: results
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Step 1: 下载模板
  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${config.path}/import-template`, {
        credentials: 'include'
      });
      if (!response.ok) {
        let message = `下载模板失败（HTTP ${response.status}）`;
        const contentType = response.headers.get('content-type') || '';
        try {
          if (contentType.includes('application/json')) {
            const body = await response.json();
            message = body.message || body.error || body.code || message;
          } else {
            const text = await response.text();
            if (text) message = text.slice(0, 160);
          }
        } catch {
          // Keep the HTTP status fallback.
        }
        throw new Error(message);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = config.filename || `${entity}-import-template.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || '下载模板失败');
    }
  };

  // Step 1 -> Step 2: 直接导入（无 dry-run）
  const handleImport = async () => {
    if (!file) {
      setError('请选择文件');
      return;
    }

    setImporting(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.upload(`${config.path}/import-excel`, formData);

      setResult(response);
      setStep(2);

      // 3秒后自动关闭 + 刷新
      setTimeout(() => {
        onComplete && onComplete();
      }, 3000);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message || `导入失败: ${err.status}`);
        if ((err.code === 'VALIDATION_ERROR' || err.status === 422) && err.details?.errors) {
          // 后端返回详细错误（422）
          setResult({
            success: false,
            errors: err.details.errors,
            total: err.details.total
          });
          setStep(2);
        }
      } else {
        setError(err.message || '导入失败');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (importing) return;
    if (typeof onClose === 'function') {
      onClose();
      return;
    }
    if (typeof onComplete === 'function') onComplete();
  };

  // 重置（回到 step 1）
  const handleReset = () => {
    setStep(1);
    setFile(null);
    setResult(null);
    setError(null);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">
            导入{config.label}
          </h2>
          <button
            onClick={handleClose}
            disabled={importing}
            className="text-slate-400 hover:text-slate-600"
            aria-label="关闭"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {error && (
            <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-slate-700 mb-2">第 1 步：准备数据</h3>
                <button
                  onClick={handleDownloadTemplate}
                  className="mb-4 px-4 py-2 border border-chango-700 text-chango-700 rounded-md hover:bg-chango-50"
                >
                  📥 下载导入模板
                </button>
                <p className="text-sm text-slate-600">
                  模板包含表头和示例数据。您可以根据模板填写数据，或从现有数据复制粘贴。
                </p>
              </div>

              <div>
                <h3 className="font-medium text-slate-700 mb-2">第 2 步：选择文件并导入</h3>
                <div className="border-2 border-dashed border-slate-300 rounded-md p-6 text-center">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      setFile(e.target.files?.[0] || null);
                      setError(null);
                    }}
                    className="block w-full"
                  />
                  {file && (
                    <p className="mt-2 text-sm text-slate-600">
                      已选择: <strong>{file.name}</strong>
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={handleClose}
                  disabled={importing}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md"
                >
                  取消
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing || !file}
                  className="px-4 py-2 bg-chango-700 text-white rounded-md hover:bg-chango-800 disabled:opacity-50"
                >
                  {importing ? '导入中...' : '开始导入'}
                </button>
              </div>
            </div>
          )}

          {step === 2 && result && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-slate-700 mb-2">导入结果</h3>
                {result.success ? (
                  <div className="p-4 bg-green-50 rounded-md border border-green-200">
                    <p className="text-sm text-green-700 font-medium">✓ 导入成功</p>
                    <p className="text-sm text-green-600 mt-1">
                      成功导入 {result.inserted} 条新数据{result.updated > 0 ? `，更新 ${result.updated} 条既有数据` : ''}。
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-rose-50 rounded-md border border-rose-200">
                    <p className="text-sm text-rose-700 font-medium">✗ 导入失败</p>
                    <p className="text-sm text-rose-600 mt-1">
                      {result.errors?.length || 0} 行存在验证错误。请修正后重新上传。
                    </p>
                  </div>
                )}
              </div>

              {result.errors && result.errors.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2 text-sm">错误详情</h4>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-slate-600">行号</th>
                          <th className="px-3 py-2 text-left text-slate-600">列</th>
                          <th className="px-3 py-2 text-left text-slate-600">错误</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.errors.map((err, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-rose-600">{err.row}</td>
                            <td className="px-3 py-2 text-rose-600">{err.col || '—'}</td>
                            <td className="px-3 py-2 text-rose-700">{err.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                {result.success ? (
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 bg-chango-700 text-white rounded-md hover:bg-chango-800"
                  >
                    关闭
                  </button>
                ) : (
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 text-slate-700 hover:bg-slate-50 rounded-md"
                  >
                    返回修改
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
